import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Megaphone, X } from "@phosphor-icons/react";
import api from "@/api";
import { isConferenceOver } from "@/lib/conference";

const STORAGE_KEY = "dftb26.banner.dismissed";
// During the live event we poll every 60 s so paper-release announcements
// surface quickly. Once the conference is over we slow this right down to
// 10 minutes so the background traffic does not keep hammering the worker.
const POLL_MS_LIVE = 60_000;
const POLL_MS_POST = 10 * 60_000;
const FRESH_MS = 60 * 60 * 1000; // only banner items <= 60 min old

function loadDismissed() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}
function saveDismissed(ids) {
  // keep latest 50 ids only to avoid unbounded growth
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(-50)));
}

/**
 * Sticky cyan banner that surfaces the most recent "fresh" announcement
 * (typically a paper-release or admin broadcast) to every delegate -
 * including those who declined push notifications. Dismissible per-device;
 * dismissed IDs persist in localStorage.
 */
export default function TopBanner() {
  const navigate = useNavigate();
  const [item, setItem] = useState(null);

  useEffect(() => {
    let alive = true;
    let iv = null;
    const tick = async () => {
      // Skip the network call entirely when the tab/PWA is in the background -
      // delegates listening to a talk don't need us pinging the server.
      if (document.visibilityState !== "visible") return;
      try {
        const r = await api.get("/announcements");
        if (!alive) return;
        const now = Date.now();
        const dismissed = new Set(loadDismissed());
        // newest first - the API sorts descending by sent_at already
        const next = (r.data || []).find((a) => {
          if (!a.sent_at || dismissed.has(a.id)) return false;
          const age = now - new Date(a.sent_at).getTime();
          return age >= 0 && age <= FRESH_MS;
        });
        setItem(next || null);
      } catch {
        // network blips during the conference are common - stay quiet
      }
    };
    tick();
    iv = setInterval(tick, isConferenceOver() ? POLL_MS_POST : POLL_MS_LIVE);
    // Refresh immediately when the user returns to the tab so the banner
    // surfaces any release that happened while they were elsewhere.
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      if (iv) clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  if (!item) return null;

  const dismiss = (e) => {
    e?.stopPropagation();
    const ids = loadDismissed();
    if (!ids.includes(item.id)) {
      ids.push(item.id);
      saveDismissed(ids);
    }
    setItem(null);
  };

  const open = () => {
    const target = item.url || "/announcements";
    if (/^https?:\/\//.test(target)) {
      window.open(target, "_blank", "noopener,noreferrer");
    } else {
      navigate(target);
    }
    dismiss();
  };

  return (
    <div
      role="alert"
      data-testid="top-banner"
      onClick={open}
      className="sticky top-0 z-40 bg-[#43C4E3] text-[#000000] cursor-pointer animate-in fade-in slide-in-from-top-2 duration-300"
    >
      <div className="max-w-3xl mx-auto px-3 py-2.5 flex items-start gap-2.5">
        <Megaphone size={18} weight="duotone" className="mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-display text-sm leading-tight truncate" data-testid="top-banner-title">
            {item.title}
          </div>
          {item.body && (
            <div className="text-xs leading-snug line-clamp-2 mt-0.5" data-testid="top-banner-body">
              {item.body}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="w-7 h-7 grid place-items-center rounded-sm hover:bg-[#2DA8C7]/40 flex-shrink-0"
          aria-label="Dismiss"
          data-testid="top-banner-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
